/**
 * PURPOSE: Piezas visuales compartidas del módulo de Resultados de Atletismo.
 * RESPONSIBILITIES: Insignias de estado (prueba, serie, fila, medalla), interruptor, control
 *   segmentado y los estados de carga, vacío y error de cada panel.
 *
 *   NUNCA SOLO COLOR. Cada insignia lleva texto: el estado se lee igual sin distinguir colores.
 * COLLABORATORS: todos los componentes de area/AtletismoResultadosArea.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/ui.jsx
 */
import React from "react";
import { FiAlertTriangle, FiInbox, FiRefreshCw } from "react-icons/fi";

import {
  ENTRY_STATUS_LABELS,
  ENTRY_STATUS_TITLES,
  MEDAL_LABELS,
  PRUEBA_STATUS_LABELS,
  ROUND_STATUS_LABELS,
} from "../labels.js";

const TONE_CLASS = {
  slate: "border-slate-200 bg-slate-100 text-slate-600",
  blue: "border-blue-200 bg-blue-50 text-blue-700",
  indigo: "border-indigo-200 bg-indigo-50 text-indigo-700",
  green: "border-emerald-200 bg-emerald-50 text-emerald-700",
  amber: "border-amber-200 bg-amber-50 text-amber-700",
  red: "border-rose-200 bg-rose-50 text-rose-700",
  violet: "border-violet-200 bg-violet-50 text-violet-700",
  gold: "border-yellow-300 bg-yellow-100 text-yellow-800",
  silver: "border-slate-300 bg-slate-100 text-slate-700",
  bronze: "border-orange-300 bg-orange-100 text-orange-800",
};

export const Badge = ({ tone = "slate", icon: Icon = null, title, children, className = "" }) => (
  <span
    title={title}
    className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${
      TONE_CLASS[tone] || TONE_CLASS.slate
    } ${className}`}
  >
    {Icon ? <Icon className="text-xs" aria-hidden="true" /> : null}
    {children}
  </span>
);

const PRUEBA_STATUS_TONE = { pending: "slate", in_progress: "amber", official: "green" };

export const PruebaStatusChip = ({ status }) => (
  <Badge tone={PRUEBA_STATUS_TONE[status] || "slate"}>
    {PRUEBA_STATUS_LABELS[status] || status || "Sin estado"}
  </Badge>
);

const ROUND_STATUS_TONE = { pending: "slate", in_progress: "amber", done: "green" };

export const RoundStatusChip = ({ status }) => (
  <Badge tone={ROUND_STATUS_TONE[status] || "slate"}>
    {ROUND_STATUS_LABELS[status] || status || "Pendiente"}
  </Badge>
);

const ENTRY_STATUS_TONE = { OK: "green", DNS: "slate", DNF: "amber", DQ: "red", NM: "slate", NH: "slate" };

export const EntryStatusChip = ({ status }) =>
  status ? (
    <Badge tone={ENTRY_STATUS_TONE[status] || "slate"} title={ENTRY_STATUS_TITLES[status]}>
      {ENTRY_STATUS_LABELS[status] || status}
    </Badge>
  ) : (
    <span className="text-xs text-slate-400">—</span>
  );

const MEDAL_TONE = { 1: "gold", 2: "silver", 3: "bronze" };

export const MedalChip = ({ medal }) =>
  MEDAL_LABELS[medal] ? <Badge tone={MEDAL_TONE[medal]}>{MEDAL_LABELS[medal]}</Badge> : null;

export const DemoBadge = () => (
  <Badge tone="violet" title="Prueba de demostración">
    DEMO
  </Badge>
);

export const Spinner = ({ className = "h-4 w-4" }) => (
  <svg className={`${className} animate-spin`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l4-4-4-4v4a12 12 0 00-12 12h4z" />
  </svg>
);

export const LoadingState = ({ label = "Cargando..." }) => (
  <div className="flex items-center justify-center gap-2 py-10 text-sm font-semibold text-slate-500">
    <Spinner />
    {label}
  </div>
);

export const EmptyState = ({ title, description, icon: Icon = FiInbox, children }) => (
  <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
    <Icon className="text-3xl text-slate-300" aria-hidden="true" />
    <p className="text-sm font-bold text-slate-600">{title}</p>
    {description ? <p className="max-w-md text-xs text-slate-500">{description}</p> : null}
    {children}
  </div>
);

export const ErrorState = ({ message, onRetry }) => (
  <div className="flex flex-col items-center justify-center gap-3 px-4 py-10 text-center">
    <FiAlertTriangle className="text-3xl text-rose-400" aria-hidden="true" />
    <p className="max-w-md text-sm font-semibold text-rose-700">{message}</p>
    {onRetry ? (
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center gap-2 rounded-lg bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-700 transition hover:bg-indigo-100"
      >
        <FiRefreshCw aria-hidden="true" /> Reintentar
      </button>
    ) : null}
  </div>
);

export const Switch = ({ checked, onChange, disabled = false, label, id }) => (
  <label
    htmlFor={id}
    className={`inline-flex items-center gap-3 ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
  >
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={Boolean(checked)}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${
        checked ? "bg-indigo-600" : "bg-slate-300"
      } disabled:cursor-not-allowed`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
          checked ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </button>
    {label ? <span className="text-sm font-semibold text-slate-700">{label}</span> : null}
  </label>
);

export const SegmentedControl = ({ options, value, onChange, disabled = false, size = "md", ariaLabel }) => (
  <div
    role="radiogroup"
    aria-label={ariaLabel}
    className={`inline-flex flex-wrap rounded-xl bg-slate-100 p-1 ${disabled ? "opacity-60" : ""}`}
  >
    {options.map((option) => {
      const selected = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={selected}
          disabled={disabled || option.disabled}
          onClick={() => !selected && onChange(option.value)}
          className={`rounded-lg font-bold transition ${
            size === "sm" ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm"
          } ${
            selected
              ? "bg-white text-indigo-700 shadow"
              : "text-slate-500 hover:text-slate-700 disabled:hover:text-slate-500"
          } disabled:cursor-not-allowed`}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

export const Card = ({ children, className = "" }) => (
  <section className={`rounded-2xl bg-white shadow-lg ${className}`}>{children}</section>
);

const selectBaseClass =
  "rounded-lg border border-slate-300 bg-white px-2 text-sm font-semibold text-slate-700 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-slate-50";

export const selectClass = `${selectBaseClass} py-1.5`;

/** Selector compacto para celdas de grilla. */
export const compactSelectClass = `${selectBaseClass} py-1`;

export const primaryButtonClass =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300";

export const secondaryButtonClass =
  "inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50";

export const dangerButtonClass =
  "inline-flex items-center justify-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-bold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50";
